import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { logError } from '@/lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WIPE_PHRASE = 'WIPE ALL DATA';
const CANCEL_PHRASE = 'CANCEL ALL STRIPE';

const fmtInt = (n) => (n ?? 0).toLocaleString();

const StripeSummary = ({ stripe }) => {
  if (!stripe) return null;
  return (
    <div className="p-4 rounded-lg bg-stone-50 border border-stone-200 text-sm text-stone-700">
      <p className="font-medium text-stone-900 mb-1">Stripe subscriptions</p>
      <p>
        Attempted: {stripe.attempted} · Canceled: {stripe.canceled} · Errors: {stripe.errors}
      </p>
      {!stripe.stripe_configured && (
        <p className="text-xs text-stone-500 mt-1">Stripe secret key not configured — no cancellations were attempted.</p>
      )}
      {stripe.errors > 0 && (
        <p className="text-xs text-stone-500 mt-1">
          Errors usually mean the subscription was already canceled or the ID was stale — safe to ignore unless you expected all to cancel cleanly.
        </p>
      )}
    </div>
  );
};

const CancelStripeCard = () => {
  const [phrase, setPhrase] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const canRun = phrase.trim() === CANCEL_PHRASE && !running;

  const runCancel = async () => {
    if (!canRun) return;
    setRunning(true);
    setResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/cancel-all-stripe-subs`,
        null,
        { params: { confirm: 'CANCEL_ALL_STRIPE' } }
      );
      setResult(res.data);
      setPhrase('');
      const s = res.data?.stripe || {};
      toast.success(`Canceled ${s.canceled || 0} of ${s.attempted || 0} Stripe subscription(s).`);
    } catch (err) {
      logError('Stripe cancel-all failed:', err?.response?.status);
      toast.error(err?.response?.data?.detail || 'Cancel failed. Try again in a moment.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card className="p-6 border-amber-200 bg-amber-50/40" data-testid="cancel-stripe-card">
      <div className="flex items-start gap-3">
        <CreditCard className="w-6 h-6 text-amber-700 shrink-0 mt-0.5" />
        <div className="flex-1 space-y-4">
          <div>
            <h3 className="text-lg font-medium text-amber-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Cancel all Stripe subscriptions
            </h3>
            <p className="text-sm text-amber-900/80 mt-1">
              Cancels every active subscription in Stripe so no one gets billed again. <strong>Does not delete any app data.</strong> Use this first if you want to silence Stripe&apos;s cancellation emails in your Stripe Dashboard before running the full wipe below.
            </p>
          </div>

          <div className="p-3 rounded bg-white border border-amber-200 text-sm text-amber-900">
            Stripe will send &quot;your subscription was canceled&quot; emails to every customer as this runs — unless you first disable those in <strong>Stripe Dashboard → Settings → Emails</strong>.
          </div>

          <div>
            <label className="text-sm font-medium text-stone-900 block mb-2">
              To confirm, type <span className="font-mono bg-stone-100 px-1.5 py-0.5 rounded">{CANCEL_PHRASE}</span> below:
            </label>
            <Input
              type="text"
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              placeholder={CANCEL_PHRASE}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              disabled={running}
              className="max-w-md font-mono"
              data-testid="cancel-stripe-confirm-input"
            />
          </div>

          <Button
            onClick={runCancel}
            disabled={!canRun}
            className="bg-amber-600 hover:bg-amber-700 text-white rounded-full px-6 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="cancel-stripe-confirm-button"
          >
            {running ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Cancelling…
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4 mr-2" />
                Cancel all Stripe subscriptions
              </>
            )}
          </Button>

          {result && (
            <div className="mt-4" data-testid="cancel-stripe-result">
              <StripeSummary stripe={result.stripe} />
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

const WipeAllCard = () => {
  const [phrase, setPhrase] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const canRun = phrase.trim() === WIPE_PHRASE && !running;

  const runWipe = async () => {
    if (!canRun) return;
    setRunning(true);
    setResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/wipe-all-data`,
        null,
        { params: { confirm: 'WIPE_ALL_DATA' } }
      );
      setResult(res.data);
      setPhrase('');
      toast.success('All customer data has been wiped.');
    } catch (err) {
      logError('Wipe failed:', err?.response?.status);
      toast.error(err?.response?.data?.detail || 'Wipe failed. Try again in a moment.');
    } finally {
      setRunning(false);
    }
  };

  const deleted = result?.deleted || {};
  const rows = Object.entries(deleted).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]);

  return (
    <Card className="p-6 border-red-200 bg-red-50/40" data-testid="wipe-all-card">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-6 h-6 text-red-600 shrink-0 mt-0.5" />
        <div className="flex-1 space-y-4">
          <div>
            <h3 className="text-lg font-medium text-red-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Wipe all customer data
            </h3>
            <p className="text-sm text-red-800/80 mt-1">
              Permanently deletes every user account and everything they created — chats with Sage, journal entries, Inner World reflections, Circle posts, payment records, referrals, waitlist entries. Active Stripe subscriptions are canceled first. <strong>Your admin login is not affected.</strong>
            </p>
          </div>

          <div className="p-3 rounded bg-white border border-red-200 text-sm text-red-900">
            This action is <strong>irreversible</strong>. There is no undo, no soft-delete, no trash bin.
          </div>

          <div>
            <label className="text-sm font-medium text-stone-900 block mb-2">
              To confirm, type <span className="font-mono bg-stone-100 px-1.5 py-0.5 rounded">{WIPE_PHRASE}</span> below:
            </label>
            <Input
              type="text"
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              placeholder={WIPE_PHRASE}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              disabled={running}
              className="max-w-md font-mono"
              data-testid="wipe-confirm-input"
            />
          </div>

          <Button
            onClick={runWipe}
            disabled={!canRun}
            className="bg-red-600 hover:bg-red-700 text-white rounded-full px-6 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="wipe-confirm-button"
          >
            {running ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Wiping…
              </>
            ) : (
              <>
                <AlertTriangle className="w-4 h-4 mr-2" />
                Wipe all customer data
              </>
            )}
          </Button>

          {result && (
            <div className="mt-6 space-y-4" data-testid="wipe-result">
              <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200">
                <p className="text-emerald-900 font-medium">
                  Done. Deleted {fmtInt(result.total_deleted)} record{result.total_deleted === 1 ? '' : 's'} across {rows.length} collections.
                </p>
              </div>

              <StripeSummary stripe={result.stripe} />

              {rows.length > 0 && (
                <details className="text-sm text-stone-600">
                  <summary className="cursor-pointer hover:text-stone-900">Per-collection breakdown</summary>
                  <ul className="mt-2 space-y-1 pl-4">
                    {rows.map(([coll, n]) => (
                      <li key={coll} className="flex justify-between border-b border-stone-100 py-1">
                        <span className="font-mono">{coll}</span>
                        <span className="tabular-nums">{fmtInt(n)}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              <p className="text-xs text-stone-400">
                Kept (system data, no PII): {(result.kept_collections || []).join(', ')}
              </p>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export const DangerZoneView = () => {
  return (
    <div className="space-y-6" data-testid="danger-zone-view">
      <div className="p-4 rounded-lg bg-stone-50 border border-stone-200 text-sm text-stone-700">
        <p className="font-medium text-stone-900 mb-1">Recommended order</p>
        <ol className="list-decimal list-inside space-y-1 text-stone-600">
          <li>Optional: turn off Stripe&apos;s automated emails in Stripe Dashboard → Settings → Emails.</li>
          <li>Cancel all Stripe subscriptions using the amber button below.</li>
          <li>Wipe all customer data using the red button below.</li>
        </ol>
      </div>

      <CancelStripeCard />
      <WipeAllCard />
    </div>
  );
};

export default DangerZoneView;
